import React, { useState } from 'react';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush
} from 'recharts';

// Import sample data
import { 
  monthlySalesByCategory, 
  weeklySalesByCategory, 
  monthlySalesByRegion 
} from '../../data/timeData';
import { productCategoriesL1 } from '../../data/productData';
import { regions } from '../../data/geoData';

const TimeSeriesAnalysis = () => {
  const [timeGranularity, setTimeGranularity] = useState('monthly');
  const [dimension, setDimension] = useState('category');
  const [selectedCategories, setSelectedCategories] = useState(['Electronics', 'Clothing', 'Beauty']);
  const [selectedRegions, setSelectedRegions] = useState(['North America', 'Europe', 'Asia']);
  const [dateRange, setDateRange] = useState('12m');
  const [chartType, setChartType] = useState('line');
  
  // Handle category selection toggle
  const toggleCategory = (category) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(cat => cat !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };
  
  // Handle region selection toggle
  const toggleRegion = (region) => {
    if (selectedRegions.includes(region)) {
      setSelectedRegions(selectedRegions.filter(reg => reg !== region));
    } else {
      setSelectedRegions([...selectedRegions, region]);
    }
  };
  
  // Get data based on current selections
  const getTimeSeriesData = () => {
    let data;
    
    // Select base data based on time granularity and dimension
    if (timeGranularity === 'monthly') {
      data = dimension === 'category' ? monthlySalesByCategory : monthlySalesByRegion;
    } else { // weekly
      data = weeklySalesByCategory; // Using only category dimension for weekly data in this POC
    }
    
    // Filter based on date range
    switch (dateRange) {
      case '3m':
        data = data.slice(0, 3);
        break;
      case '6m':
        data = data.slice(0, 6);
        break;
      case '12m':
      default:
        // Use all available data
        break;
    }
    
    return data;
  };
  
  // Get categories to display based on selected dimension
  const getDisplayedCategories = () => {
    if (dimension === 'category') {
      return productCategoriesL1
        .map(cat => cat.name)
        .filter(cat => selectedCategories.includes(cat));
    } else { // region
      return regions
        .map(reg => reg.name)
        .filter(reg => selectedRegions.includes(reg));
    }
  };
  
  // Get available categories based on current dimension
  const getAvailableCategories = () => {
    if (dimension === 'category') {
      return productCategoriesL1.map(cat => cat.name);
    } else { // region
      return regions.map(reg => reg.name);
    }
  };
  
  // Dynamic colors for chart lines
  const getLineColor = (index) => {
    const colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#8DD1E1', '#A4DE6C', '#D0ED57', '#FFAA33'];
    return colors[index % colors.length];
  };
  
  // Render appropriate chart based on selected type
  const renderChart = () => {
    const data = getTimeSeriesData();
    const categories = getDisplayedCategories();
    
    if (categories.length === 0) {
      return <div className="text-center py-12 text-gray-500">Select at least one {dimension} to display</div>;
    }
    
    const dateKey = timeGranularity === 'monthly' ? 'month' : 'week';
    
    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={500}>
            <LineChart
              data={data}
              margin={{ top: 5, right: 30, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={dateKey} />
              <YAxis />
              <Tooltip formatter={(value) => `$${(value/1000000).toFixed(2)}M`} />
              <Legend />
              <Brush dataKey={dateKey} height={30} stroke="#8884d8" />
              
              {categories.map((category, index) => (
                <Line 
                  key={category}
                  type="monotone"
                  dataKey={category}
                  stroke={getLineColor(index)}
                  activeDot={{ r: 8 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
        
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={500}>
            <BarChart
              data={data}
              margin={{ top: 5, right: 30, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={dateKey} />
              <YAxis />
              <Tooltip formatter={(value) => `$${(value/1000000).toFixed(2)}M`} />
              <Legend />
              <Brush dataKey={dateKey} height={30} stroke="#8884d8" />
              
              {categories.map((category, index) => (
                <Bar 
                  key={category}
                  dataKey={category}
                  fill={getLineColor(index)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
        
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={500}>
            <AreaChart
              data={data}
              margin={{ top: 5, right: 30, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={dateKey} />
              <YAxis />
              <Tooltip formatter={(value) => `$${(value/1000000).toFixed(2)}M`} />
              <Legend />
              <Brush dataKey={dateKey} height={30} stroke="#8884d8" />
              
              {categories.map((category, index) => (
                <Area 
                  key={category}
                  type="monotone"
                  dataKey={category}
                  stackId="1"
                  stroke={getLineColor(index)}
                  fill={getLineColor(index)}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );
        
      case 'composed':
        return (
          <ResponsiveContainer width="100%" height={500}>
            <ComposedChart
              data={data}
              margin={{ top: 5, right: 30, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={dateKey} />
              <YAxis />
              <Tooltip formatter={(value) => `$${(value/1000000).toFixed(2)}M`} />
              <Legend />
              <Brush dataKey={dateKey} height={30} stroke="#8884d8" />
              
              {categories.map((category, index) => (
                index % 2 === 0 ? (
                  <Line 
                    key={category}
                    type="monotone"
                    dataKey={category}
                    stroke={getLineColor(index)}
                    activeDot={{ r: 8 }}
                  />
                ) : (
                  <Bar 
                    key={category}
                    dataKey={category}
                    fill={getLineColor(index)}
                  />
                )
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        );
        
      default:
        return null;
    }
  };
  
  // Calculate growth rates between first and last periods
  const calculateGrowthRates = () => {
    const data = getTimeSeriesData();
    const categories = getDisplayedCategories();
    
    if (data.length < 2 || categories.length === 0) {
      return [];
    }
    
    const firstPeriod = data[data.length - 1]; // Oldest period (e.g., last year)
    const lastPeriod = data[0]; // Newest period (e.g., current month)
    
    return categories.map(category => {
      const startValue = firstPeriod[category] || 0;
      const endValue = lastPeriod[category] || 0;
      const growthRate = startValue > 0 
        ? ((endValue - startValue) / startValue) * 100 
        : 0;
        
      return {
        category,
        startValue,
        endValue,
        change: endValue - startValue,
        growthRate
      };
    });
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Time Series Analysis</h1>
        <p className="text-gray-600 mb-4">
          Analyze sales performance over time using different time granularities, dimensions, and visualization types.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Time Granularity</label>
            <div className="flex">
              <button
                onClick={() => setTimeGranularity('monthly')}
                className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                  timeGranularity === 'monthly' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setTimeGranularity('weekly')}
                className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                  timeGranularity === 'weekly' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Weekly
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Analysis Dimension</label>
            <div className="flex">
              <button
                onClick={() => setDimension('category')}
                className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                  dimension === 'category' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Product Categories
              </button>
              <button
                onClick={() => setDimension('region')}
                disabled={timeGranularity === 'weekly'}
                className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                  dimension === 'region' 
                    ? 'bg-blue-600 text-white' 
                    : timeGranularity === 'weekly'
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Geographic Regions
              </button>
            </div>
            {timeGranularity === 'weekly' && dimension === 'region' && (
              <p className="text-xs text-amber-600 mt-1">Weekly data is only available for product categories</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Date Range</label>
            <div className="flex">
              <button
                onClick={() => setDateRange('3m')}
                className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                  dateRange === '3m' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                3 Months
              </button>
              <button
                onClick={() => setDateRange('6m')}
                className={`px-3 py-2 text-sm font-medium ${
                  dateRange === '6m' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                6 Months
              </button>
              <button
                onClick={() => setDateRange('12m')}
                className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                  dateRange === '12m' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                12 Months
              </button>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Chart Type</label>
            <div className="flex flex-wrap">
              <button
                onClick={() => setChartType('line')}
                className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                  chartType === 'line' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Line
              </button>
              <button
                onClick={() => setChartType('bar')}
                className={`px-3 py-2 text-sm font-medium ${
                  chartType === 'bar' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Bar
              </button>
              <button
                onClick={() => setChartType('area')}
                className={`px-3 py-2 text-sm font-medium ${
                  chartType === 'area' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Area
              </button>
              <button
                onClick={() => setChartType('composed')}
                className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                  chartType === 'composed' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Composed
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">
              {dimension === 'category' ? 'Product Categories' : 'Geographic Regions'}
            </label>
            <div className="flex flex-wrap gap-2">
              {getAvailableCategories().map(category => (
                <button
                  key={category}
                  onClick={() => dimension === 'category' ? toggleCategory(category) : toggleRegion(category)}
                  className={`px-3 py-1 text-xs font-medium rounded-full ${
                    (dimension === 'category' ? selectedCategories : selectedRegions).includes(category)
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Chart visualization */}
      <div className="border border-gray-200 rounded-lg p-4">
        {renderChart()}
      </div>
      
      {/* Growth analysis */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Growth Analysis</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{dimension === 'category' ? 'Category' : 'Region'}</th>
                <th>Start Value</th>
                <th>Current Value</th>
                <th>Change</th>
                <th>Growth Rate</th>
              </tr>
            </thead>
            <tbody>
              {calculateGrowthRates().map(({ category, startValue, endValue, change, growthRate }) => (
                <tr key={category}>
                  <td>{category}</td>
                  <td>${(startValue / 1000000).toFixed(2)}M</td>
                  <td>${(endValue / 1000000).toFixed(2)}M</td>
                  <td className={change >= 0 ? 'positive-growth' : 'negative-growth'}>
                    {change >= 0 ? '+' : ''}{(change / 1000000).toFixed(2)}M
                  </td>
                  <td className={growthRate >= 0 ? 'positive-growth' : 'negative-growth'}>
                    {growthRate >= 0 ? '+' : ''}{growthRate.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TimeSeriesAnalysis;